import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FaWater, FaUtensils, FaDumbbell, FaCapsules } from "react-icons/fa";

const remindersList = [
  {
    title: "Water Intake",
    icon: <FaWater className="text-blue-400 text-4xl" />,
    desc: "Stay hydrated with timely sips throughout the day.",
    bg: "from-blue-900/30 to-blue-800/10",
  },
  {
    title: "Meal Times",
    icon: <FaUtensils className="text-green-400 text-4xl" />,
    desc: "Never skip a meal — get smart nudges when it’s time to eat.",
    bg: "from-green-900/30 to-green-800/10",
  },
  {
    title: "Workout Schedules",
    icon: <FaDumbbell className="text-red-400 text-4xl" />,
    desc: "Keep moving with personalized training notifications.",
    bg: "from-red-900/30 to-red-800/10",
  },
  {
    title: "Supplements",
    icon: <FaCapsules className="text-yellow-400 text-4xl" />,
    desc: "Track and remind your supplement intake seamlessly.",
    bg: "from-yellow-900/30 to-yellow-800/10",
  },
];

export default function FeaturePersonalReminders() {
  const [selectedReminder, setSelectedReminder] = useState(null);
  const [time, setTime] = useState("");
  const [interval, setInterval] = useState("");
  const [savedReminders, setSavedReminders] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate loading delay (e.g. fetching from server)
    const timer = setTimeout(() => setIsLoading(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  const openModal = (index) => {
    setSelectedReminder(index);
    setTime(savedReminders[index]?.time || "");
    setInterval(savedReminders[index]?.interval || "");
  };

  const saveReminder = () => {
    setSavedReminders((prev) => ({
      ...prev,
      [selectedReminder]: { time, interval },
    }));
    setSelectedReminder(null);
  };

  return (
    <section className="min-h-screen bg-gradient-to-br from-[#0b1410] via-[#0e1a10] to-[#0b1410] text-white px-6 py-20 font-manrope relative overflow-hidden">
      <div className="max-w-6xl mx-auto z-10 relative">
        <motion.h1
          className="text-4xl sm:text-5xl font-bold text-green-400 mb-10 text-center"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          Personalized Reminders
        </motion.h1>

        <motion.p
          className="text-lg text-gray-300 text-center max-w-3xl mx-auto mb-16"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
        >
          Tap a reminder below to customize the time and frequency that fits your daily schedule.
        </motion.p>

        <div className="grid md:grid-cols-2 gap-10">
          {isLoading
            ? Array(4)
                .fill(0)
                .map((_, index) => (
                  <div
                    key={index}
                    className="animate-pulse bg-white/5 rounded-3xl p-8 h-56 space-y-4"
                  >
                    <div className="flex gap-4 items-center">
                      <div className="bg-white/10 rounded-xl h-12 w-12" />
                      <div className="h-4 w-2/3 bg-gray-600 rounded" />
                    </div>
                    <div className="h-4 bg-gray-600 rounded w-full"></div>
                    <div className="h-4 bg-gray-600 rounded w-1/2"></div>
                    <div className="h-8 bg-green-600/30 rounded w-1/3 mt-4"></div>
                  </div>
                ))
            : remindersList.map((item, index) => (
                <motion.div
                  key={index}
                  className={`bg-gradient-to-br ${item.bg} border border-gray-700 p-8 rounded-3xl shadow-2xl hover:scale-[1.02] transition-all duration-500`}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2 }}
                  viewport={{ once: true }}
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="bg-white/10 p-4 rounded-xl">{item.icon}</div>
                    <h3 className="text-2xl font-semibold text-white">{item.title}</h3>
                  </div>
                  <p className="text-gray-300 mb-4">{item.desc}</p>

                  {savedReminders[index] && (
                    <p className="text-sm text-green-400 mb-2">
                      ✅ Reminder at {savedReminders[index].time} every{" "}
                      {savedReminders[index].interval} hrs
                    </p>
                  )}

                  <button
                    onClick={() => openModal(index)}
                    className="mt-4 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-full transition duration-300"
                  >
                    Set Reminder
                  </button>
                </motion.div>
              ))}
        </div>
      </div>

      {/* Modal */}
      {selectedReminder !== null && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center px-4">
          <div className="bg-[#1a2a1f] text-white w-full max-w-md rounded-xl p-8 border border-green-500 relative">
            <h2 className="text-2xl font-bold text-green-400 mb-4">
              Set Reminder — {remindersList[selectedReminder].title}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Reminder Time</label>
                <input
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  className="w-full bg-transparent border border-gray-600 px-4 py-2 rounded-md focus:outline-none focus:ring-1 focus:ring-green-400 text-white"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Repeat Interval (hrs)</label>
                <input
                  type="number"
                  min="1"
                  placeholder="e.g. 3"
                  value={interval}
                  onChange={(e) => setInterval(e.target.value)}
                  className="w-full bg-transparent border border-gray-600 px-4 py-2 rounded-md focus:outline-none focus:ring-1 focus:ring-green-400 text-white"
                />
              </div>

              <div className="flex justify-end gap-4 mt-6">
                <button
                  onClick={() => setSelectedReminder(null)}
                  className="px-4 py-2 text-sm bg-gray-600 hover:bg-gray-700 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={saveReminder}
                  className="px-4 py-2 text-sm bg-green-500 hover:bg-green-600 rounded-md"
                >
                  Save Reminder
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Background bubbles */}
      <div className="absolute inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-32 h-32 bg-green-300 rounded-full blur-2xl opacity-10 animate-ping"></div>
        <div className="absolute bottom-10 right-10 w-40 h-40 bg-blue-300 rounded-full blur-2xl opacity-10 animate-pulse"></div>
        <div className="absolute bottom-40 left-[45%] w-24 h-24 bg-yellow-400 rounded-full blur-xl opacity-20 animate-bounce"></div>
      </div>
    </section>
  );
}
